<template>
  <div>
    <PageHeader title="Notebooks" subtitle="Jupyter notebooks in your S3 folder" :breadcrumbs="BASE_CRUMBS" />
    <div class="p-6 pb-32 space-y-4">
      <div class="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div class="flex items-center justify-between gap-4 px-4 py-3 border-b border-gray-200">
          <div>
            <div class="text-sm font-semibold text-gray-900">Notebooks</div>
            <div class="text-xs text-gray-600 mt-1">Your S3 folder as it is stored. Open a .ipynb file to work in it.</div>
          </div>
          <div class="flex items-center gap-2">
            <label class="btn-secondary-sm text-xs cursor-pointer" :class="{ 'opacity-60': uploading }">
              {{ uploading ? 'Uploading…' : 'Upload .ipynb' }}
              <input type="file" class="hidden" accept=".ipynb" :disabled="uploading" @change="onPick">
            </label>
            <form class="flex items-center gap-2" @submit.prevent="create">
              <input v-model="newTitle" type="text" placeholder="New notebook name" aria-label="New notebook name" required class="w-56 px-3 py-1.5 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-dark focus:ring-offset-4">
              <button type="submit" class="btn-primary-sm text-xs" :disabled="creating">{{ creating ? 'Creating…' : 'New notebook' }}</button>
            </form>
          </div>
        </div>
        <div class="flex items-center gap-1 px-4 py-2 border-b border-gray-100 text-xs">
          <router-link :to="folderPath('')" class="text-blue-dark hover:underline">All files</router-link>
          <template v-for="c in crumbs" :key="c.path">
            <span class="text-gray-400">/</span>
            <router-link :to="folderPath(c.path)" class="text-blue-dark hover:underline">{{ c.name }}</router-link>
          </template>
        </div>
        <div v-if="actionError" class="px-4 py-2 text-xs text-red-dark border-b border-gray-100">{{ actionError }} <button type="button" class="ml-2 text-gray-500" @click="actionError = ''">dismiss</button></div>
        <LoadingIndicator v-if="loading" message="Reading the folder..." />
        <div v-else-if="error" class="px-4 py-6 text-xs text-red-dark">{{ error }}</div>
        <div v-else-if="folders.length || files.length" class="max-h-[32rem] overflow-y-auto">
          <table class="w-full table-fixed border-collapse">
            <colgroup>
              <col class="w-[60%]" />
              <col class="w-[14%]" />
              <col class="w-[26%]" />
            </colgroup>
            <thead class="sticky top-0 z-10 bg-gray-25">
              <tr class="border-b border-gray-200">
                <th class="px-4 py-2 text-left text-xs font-semibold text-gray-600">Name</th>
                <th class="px-4 py-2 text-right text-xs font-semibold text-gray-600">Size</th>
                <th class="px-4 py-2 text-left text-xs font-semibold text-gray-600">Modified</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="folder" role="button" tabindex="0" class="border-b border-gray-50 cursor-pointer hover:bg-gray-25 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-dark" @click="$router.push(folderPath(parentFolder))" @keydown.enter="$router.push(folderPath(parentFolder))">
                <td class="px-4 py-2 text-xs font-semibold text-gray-900" colspan="3"><font-awesome-icon :icon="['fas', 'folder']" class="w-3.5 h-3.5 text-gray-500 mr-2" />..</td>
              </tr>
              <tr v-for="f in folders" :key="'dir:' + f.path" role="button" tabindex="0" class="border-b border-gray-50 cursor-pointer hover:bg-gray-25 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-dark" @click="$router.push(folderPath(f.path))" @keydown.enter="$router.push(folderPath(f.path))">
                <td class="px-4 py-2 text-xs font-semibold text-gray-900" colspan="3"><font-awesome-icon :icon="['fas', 'folder']" class="w-3.5 h-3.5 text-gray-500 mr-2" /><span>{{ f.name }}</span>/</td>
              </tr>
              <tr v-for="p in files" :key="p.path" :role="p.notebook ? 'button' : null" :tabindex="p.notebook ? 0 : null" class="border-b border-gray-50" :class="p.notebook ? 'cursor-pointer hover:bg-gray-25 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-dark' : ''" @click="p.notebook && open($event, p)" @auxclick="p.notebook && open($event, p)" @keydown.enter="p.notebook && open($event, p)">
                <td class="px-4 py-2 text-xs whitespace-normal break-words" :class="p.notebook ? 'font-semibold text-gray-900' : 'text-gray-500'">
                  <a v-if="p.notebook" :href="$router.resolve(notebookPath(p.path)).href" @click.stop="open($event, p)"><span>{{ p.name }}</span></a>
                  <span v-else>{{ p.name }}</span>
                </td>
                <td class="px-4 py-2 text-xs text-right text-gray-600 tabular-nums">{{ size(p.size) }}</td>
                <td class="px-4 py-2 text-xs text-gray-600 whitespace-nowrap">{{ relative(p.updated_at) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="px-4 py-10 text-center">
          <div class="text-sm text-gray-700">Nothing here.</div>
          <div class="text-xs text-gray-500 mt-1">Upload a .ipynb file or start a new notebook.</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import PageHeader from '@/components/widgetcomponents/library/PageHeader.vue';
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';
import { relative } from '@/components/Admin/Hermes/hermes';
import { isPlainClick, openInNewTabOnModifiedClick } from '@/utils/links';
import { BASE_CRUMBS, folderPath, notebookPath } from './impactNav';

export default {
  name: 'NotebookIndex',
  components: { PageHeader, LoadingIndicator },
  data() {
    return { folders: [], files: [], loading: true, error: '', actionError: '', newTitle: '', creating: false, uploading: false, BASE_CRUMBS };
  },
  computed: {
    folder() {
      return String(this.$route.query.folder || '').replace(/^\/+|\/+$/g, '');
    },
    parentFolder() {
      return this.folder.includes('/') ? this.folder.slice(0, this.folder.lastIndexOf('/')) : '';
    },
    crumbs() {
      const out = [];
      this.folder.split('/').filter(Boolean).forEach((name, i, all) => out.push({ name, path: all.slice(0, i + 1).join('/') }));
      return out;
    },
  },
  watch: {
    folder() {
      this.load();
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    relative,
    notebookPath,
    folderPath,
    size(bytes) {
      if (bytes == null) return '';
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    },
    async load() {
      this.loading = true;
      this.error = '';
      try {
        const { data } = await axios.get('api/impact_analytics/notebooks/', { params: this.folder ? { folder: this.folder } : {} });
        this.folders = data.folders;
        this.files = data.files;
      } catch (err) {
        this.error = err?.response?.data?.detail || 'The folder could not be read.';
      } finally {
        this.loading = false;
      }
    },
    open(event, p) {
      const href = this.$router.resolve(notebookPath(p.path)).href;
      if (openInNewTabOnModifiedClick(event, href)) return;
      if (!isPlainClick(event)) return;
      event.preventDefault();
      this.$router.push(notebookPath(p.path));
    },
    async create() {
      if (this.creating || !this.newTitle.trim()) return;
      this.creating = true;
      this.actionError = '';
      try {
        const { data } = await axios.post('api/impact_analytics/notebooks/', { title: this.newTitle.trim(), folder: this.folder || 'notebooks' });
        this.$router.push(notebookPath(data.id));
      } catch (err) {
        this.actionError = err?.response?.data?.error || 'The notebook could not be created.';
      } finally {
        this.creating = false;
      }
    },
    onPick(event) {
      const file = event.target.files[0];
      event.target.value = '';
      if (file) this.upload(file);
    },
    async upload(file) {
      if (this.uploading) return;
      this.uploading = true;
      this.actionError = '';
      try {
        const { data: presigned } = await axios.post('/api/etl_module/upload/', { filename: file.name, content_type: 'application/x-ipynb+json' });
        const put = await fetch(presigned.upload_url, { method: 'PUT', headers: { 'Content-Type': 'application/x-ipynb+json' }, body: file });
        if (!put.ok) throw new Error(`Upload failed (${put.status})`);
        const { data } = await axios.post('api/impact_analytics/notebooks/upload/', { s3_key: presigned.s3_key, folder: this.folder || 'notebooks' });
        this.$router.push(notebookPath(data.id));
      } catch (err) {
        this.actionError = err?.response?.data?.error || (err && err.message) || 'The notebook could not be uploaded.';
      } finally {
        this.uploading = false;
      }
    },
  },
};
</script>
